import React, { useState, useEffect, useCallback } from 'react';
import './reports.css'
import * as XLSX from 'xlsx';
import axios from 'axios';
import { ClipLoader } from "react-spinners";
import { toast } from 'sonner';
import { useAbility } from '../../roles/AbilityContext';
import { useTranslation } from 'react-i18next';
import { formatInTimeZone } from 'date-fns-tz';
import { Reports, Alertype } from '../../interfaces/interfaces';
import { getReport, getAlertype, postReport, patchReport } from '../../services/requests';
import Table, { ColumnConfig } from '../../components/table/table';

const ReportsComponent: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const ability = useAbility();
    const [data, setData] = useState([{ ID: "", Type: "", Description: "", Date: "" }]);
    const { t } = useTranslation();
    const [dataStatusReports, setDataStatusReports] = useState<Alertype[]>([]);
    const [loadingBtn, setLoadingBtn] = useState(false);

    /**
     * Formatea una cadena de fecha ISO a un formato 'YYYY-MM-DD HH:MM:SS'.
     * @param isoString - Fecha en formato ISO 8601 (con o sin 'Z' al final).
     * @returns La fecha formateada como un string 'YYYY-MM-DD HH:MM:SS'.
     */
    const formatDate = (isoString: string): string => {
        const timeZone = 'Europe/Madrid';
        return formatInTimeZone(isoString, timeZone, 'yyyy-MM-dd HH:mm:ss');
    };

    /**
     * Obtiene la información de los reportes y tipos de alerta desde el servidor, 
     * los transforma y actualiza los estados de los datos.
     * - Realiza peticiones para obtener los reportes y los tipos de alerta.
     * - Transforma los datos y filtra los reportes válidos.
     * - Actualiza el estado de los reportes y tipos de alerta.
     */
    const getInformation = useCallback(async () => {
        try {
            const response = await getReport();

            if (response.status >= 200 && response.status < 300) {
                toast.success(`${response.data.message}`);
            }

            const responseAlertype = await getAlertype();

            const transformedData = (response.data.data as Reports[])
                .filter(report => report.Nreporte !== 0)
                .map(report => {
                    const alertypeMatch = (responseAlertype.data.data as Alertype[]).find(item => item.NtipoInc === report.Tipo);

                    return {
                        ID: report.Nreporte.toString(),
                        Type: alertypeMatch ? alertypeMatch.TtipoInc : "",
                        Description: report.Treporte,
                        Date: formatDate(report.Fcreacion || "")
                    }
                });

            setData(transformedData);
            setFilteredData(transformedData);

            setDataStatusReports(responseAlertype.data.data);

        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || error;
            toast.error(`${errorMessage}`);
            setLoading(false);
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Efecto que ejecuta la función `getInformation` al cargar el componente.
     */
    useEffect(() => {
        getInformation();
    }, [getInformation]);

    const [isFullScreen, setIsFullScreen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredData, setFilteredData] = useState(data);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const totalPage = itemsPerPage === filteredData.length ? 1 : Math.ceil(filteredData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = itemsPerPage === filteredData.length ? filteredData.length : startIndex + itemsPerPage;
    const currentData = filteredData.slice(startIndex, endIndex);

    /**
     * Cambia a la siguiente página si no es la última.
     * - Incrementa el estado `currentPage` si el número actual de página es menor que el total de páginas.
     */
    const handleNextPage = () => {
        if (currentPage < totalPage) {
            setCurrentPage(currentPage + 1);
        }
    };

    /**
     * Cambia a la página anterior si no es la primera.
     * - Decrementa el estado `currentPage` si el número actual de página es mayor que 1.
     */
    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    /**
     * Descarga los datos en formato XLSX.
     * - Convierte los datos en formato JSON a una hoja de cálculo y la descarga como un archivo `Report Data.xlsx`.
     */
    const downloadDataAsXLSX = () => {
        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Report Data');
        XLSX.writeFile(workbook, 'Report Data.xlsx');
    };

    /**
     * Filtra los datos basados en un término de búsqueda.
     * - Filtra los datos de acuerdo a si alguno de sus valores contiene el `searchTerm`.
     * - Si se encuentran resultados, establece la página actual a la primera.
     */
    const handleSearch = () => {
        const filtered = data.filter((item) => {
            return Object.values(item).some(value =>
                value.toString().toLowerCase().includes(searchTerm.toLowerCase())
            );
        });
        setFilteredData(filtered);

        if (filtered.length > 0) {
            setCurrentPage(1);
        } else {
            setCurrentPage(0);
        }
    };

    const [sortConfig, setSortConfig] = useState<{
        key: keyof typeof data[0] | null;
        direction: "asc" | "desc" | null;
    }>({ key: "Date", direction: "desc" });

    /**
     * Efecto que ordena los datos por la propiedad 'Date' en orden descendente.
     * - Al detectar un cambio en los datos, ordena `filteredData` de acuerdo a la propiedad 'Date'.
     */
    useEffect(() => {
        const sortedData = [...data].sort((a, b) => (a.Date < b.Date ? 1 : -1));
        setFilteredData(sortedData);
    }, [data]);

    /**
     * Ordena los datos por una clave específica y alterna la dirección del orden.
     * @param key - La clave por la cual se debe ordenar los datos.
     * - Alterna entre el orden ascendente y descendente.
     */
    const sortData = (key: keyof typeof data[0]) => {
        let direction: "asc" | "desc" = "asc";
        if (sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }
        setSortConfig({ key, direction });

        const sortedData = [...data].sort((a, b) => {
            if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
            if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
            return 0;
        });

        setFilteredData(sortedData);
    };

    const [modalTypeNew, setModalTypeNew] = useState('');
    const [modalDescriptionNew, setModalDescriptionNew] = useState('');

    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState('');
    const [modalDescription, setModalDescription] = useState('');

    /**
     * Muestra el modal para crear un nuevo reporte.
     * - Establece el estado `showModal` como `true`, abriendo el modal.
     */
    const handleCardClick = () => {
        setShowModal(true);
    };

    /**
     * Cierra los modales y limpia el estado relacionado con la gestión de alertas:
     * - Oculta los modales de creación y edición.
     * - Restablece los valores del formulario (`ID`, `Tipo`, `Descripción`).
     * - Elimina los mensajes de error y detiene el indicador de carga del botón.
     */
    const closeModal = () => {
        setShowModal(false);
        setShowModalEdit(false);
        setSelectedID('');
        setModalID('');
        setModalType('');
        setModalDescription('');
        setLoadingBtn(false);
        setErrorType("");
        setErrorDescription("");
    };

    /**
     * Cierra el modal cuando se hace clic en el área de superposición.
     * - Resetea varios estados relacionados con el modal (como `selectedID`, `modalID`, `modalType`, etc.) y cierra el modal.
     */
    const handleOverlayClick = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).classList.contains('modalOverlay')) {
            closeModal();
        }
    };

    /**
     * Extrae un mensaje de error legible desde un campo de error genérico.
     * - Si el campo es un array, retorna el primer elemento.
     * - Si el campo es una cadena de texto, la retorna directamente.
     * - En cualquier otro caso, retorna una cadena vacía.
     *
     * @param field - El campo de error a procesar (puede ser un string o un array).
     * @returns El mensaje de error extraído o una cadena vacía si no es válido.
     */
    const extractErrorMessage = (field: any) => {
        if (Array.isArray(field)) return field[0];
        if (typeof field === 'string') return field;
        return '';
    };

    /**
     * Crea un nuevo reporte enviando los datos del modal al servidor.
     * - Verifica que todos los campos estén completos antes de enviar la solicitud.
     * - Al recibir una respuesta exitosa, muestra un mensaje de éxito y actualiza los datos.
     */
    const handleCreateReport = async () => {
        const today = new Date();
        const formattedDate = today.toISOString().slice(0, 19).replace('T', ' ');
        setLoadingBtn(true);

        if (!modalTypeNew || !modalDescriptionNew) {
            setErrorType(!modalTypeNew ? "Por favor complete este campo" : "");
            setErrorDescription(!modalDescriptionNew ? "Por favor complete este campo" : "");
            toast.error(`${t('reports.pleaseCompleteAllFields')}.`);
            setLoadingBtn(false);
            return;
        }

        try {
            const alertypeMatch = dataStatusReports.find(item => item.TtipoInc === modalTypeNew);

            const body = {
                Tipo: alertypeMatch?.NtipoInc,
                Treporte: modalDescriptionNew,
                Fcreacion: formattedDate,
                Status: null
            };

            const response = await postReport(body);

            if (response.status >= 200 && response.status < 300) {
                toast.success(`${response.data.message}`);
                setLoading(true);
                getInformation();
                closeModal();
            } else {
                toast.error(`${t('reports.theResponseWasNotSuccessful')}: ${response.status}`);
                setLoading(false);
                setLoadingBtn(false);
            }

        } catch (error: any) {
            let message = `${t('reports.errorMakingTheRequest')}: ${error}`;

            if (axios.isAxiosError(error) && error.response?.data) {
                const data = error.response.data;

                if (typeof data === 'string') {
                    message = data;
                } else if (typeof data === 'object') {
                    const firstKey = Object.keys(data)[0];
                    const value = data[firstKey];

                    if (Array.isArray(value)) {
                        message = value[0];
                    } else {
                        message = value;
                    }
                }
            } else if (error instanceof Error) {
                message = error.message;
            }

            const errorData = error?.response?.data?.error || {};
            setErrorType(extractErrorMessage(errorData.Tipo));
            setErrorDescription(extractErrorMessage(errorData.Treporte));

            toast.error(`Error: ${message}`, {
                position: "top-right"
            });

            setLoading(false);
            setLoadingBtn(false);
        }
    };

    const [showModalEdit, setShowModalEdit] = useState(false);
    const [modalID, setModalID] = useState('');
    const [selectedID, setSelectedID] = useState('');

    /**
     * Actualiza un reporte existente con los nuevos valores del modal.
     * - Verifica que todos los campos estén completos antes de enviar la solicitud.
     * - Al recibir una respuesta exitosa, muestra un mensaje de éxito y actualiza los datos.
     */
    const handleUpdateReport = async () => {
        if (!selectedID) return;

        setLoadingBtn(true);

        if (!modalType || !modalDescription) {
            setErrorType(!modalType ? "Por favor complete este campo" : "");
            setErrorDescription(!modalDescription ? "Por favor complete este campo" : "");
            toast.error(`${t('reports.pleaseCompleteAllFields')}.`);
            setLoadingBtn(false);
            return;
        }

        try {
            const alertypeMatch = dataStatusReports.find(item => item.TtipoInc === modalType);

            const body = {
                id: modalID,
                Tipo: alertypeMatch?.NtipoInc,
                Treporte: modalDescription,
                Status: null
            };

            const response = await patchReport(body);

            if (response.status >= 200 && response.status < 300) {
                toast.success(`${response.data.message}`);
                setLoading(true);
                getInformation();
                closeModal();
            } else {
                toast.error(`${t('reports.theReportCouldNotBeEditedWithID')}: ${selectedID}`);
                setLoading(false);
                setLoadingBtn(false);
            }

        } catch (error: any) {
            let message = `${t('reports.errorMakingTheRequest')}: ${error}`;

            if (axios.isAxiosError(error) && error.response?.data) {
                const data = error.response.data;

                if (typeof data === 'string') {
                    message = data;
                } else if (typeof data === 'object') {
                    const firstKey = Object.keys(data)[0];
                    const value = data[firstKey];

                    if (Array.isArray(value)) {
                        message = value[0];
                    } else {
                        message = value;
                    }
                }
            } else if (error instanceof Error) {
                message = error.message;
            }

            const errorData = error?.response?.data?.error || {};
            setErrorType(extractErrorMessage(errorData.Tipo));
            setErrorDescription(extractErrorMessage(errorData.Treporte));

            toast.error(`Error: ${message}`, {
                position: "top-right"
            });

            setLoading(false);
            setLoadingBtn(false);
        }
    };

    const [isSelectOpen, setIsSelectOpen] = useState(false);
    const handleSelectFocus = () => setIsSelectOpen(true);
    const handleSelectBlur = () => setIsSelectOpen(false);

    const [errorType, setErrorType] = useState('');
    const [errorDescription, setErrorDescription] = useState('');

    const columns: ColumnConfig[] = [
        { key: 'ID', label: 'ID', sortable: true },
        { key: 'Type', label: t('reports.type'), sortable: true },
        { key: 'Description', label: t('reports.description'), sortable: true },
        { key: 'Date', label: t('reports.date'), sortable: true },
    ];

    return (
        <div className='containerReports'>
            {loading ? (
                <div className="loading">
                    <ClipLoader color="#007bff" size={50} />
                    <p>{t('reports.loadingInformation')}</p>
                </div>
            ) : (
                <>
                    <div className='title'>
                        <h4>{t('reports.reports')}</h4>
                        <div>
                            <button className="btn btn-dark" onClick={handleCardClick}>
                                <i className="fas fa-plus"></i> {t('reports.newReport')}
                            </button>
                        </div>
                    </div>
                    <div className={`separator ${isFullScreen ? 'expanded' : ''}`}>
                        <Table
                            t={t}
                            titleKey="reports.reportsList"
                            placeholder='reports.searchReports'
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                            handleSearch={handleSearch}
                            downloadDataAsXLSX={downloadDataAsXLSX}
                            isFullScreen={isFullScreen}
                            setIsFullScreen={setIsFullScreen}

                            nameClassTable="tableReports"
                            columns={columns}
                            currentData={currentData}
                            sortConfig={sortConfig}
                            onSort={sortData}
                            onRowEdit={(row) => {
                                setShowModalEdit(true);
                                setSelectedID(row.ID);
                                setModalID(row.ID);
                                const selectedItem = filteredData.find((data) => data.ID === row.ID);
                                setModalType(selectedItem ? selectedItem.Type : "");
                                setModalDescription(selectedItem ? selectedItem.Description : "");
                            }}

                            handlePreviousPage={handlePreviousPage}
                            currentPage={currentPage}
                            totalPage={totalPage}
                            itemsPerPage={itemsPerPage}
                            filteredData={filteredData}
                            setItemsPerPage={setItemsPerPage}
                            setCurrentPage={setCurrentPage}
                            handleNextPage={handleNextPage}
                            data={data}
                        />
                    </div>

                    {(showModal || showModalEdit) && (
                        <div className='modalOverlay' onClick={handleOverlayClick}>
                            <div className='modalContent'>
                                <div className='title'>
                                    <h6>
                                        {showModal ? t('reports.newReport') : showModalEdit ? t('reports.editReport') : ''}
                                    </h6>
                                    <span className="closeIcon" onClick={() => {
                                        closeModal();
                                    }}>&times;</span>
                                </div>
                                {showModalEdit &&
                                    <div className='formInput'>
                                        <div className="input-row">
                                            <h6>ID</h6>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={modalID}
                                                placeholder="Search by ID"
                                                disabled
                                            />
                                        </div>
                                    </div>
                                }
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6><span>*</span> {t('reports.type')}</h6>
                                        <div className="select-wrapper">
                                            <select
                                                className={`form-control ${errorType ? 'is-invalid' : ''}`}
                                                value={showModal ? modalTypeNew : modalType}
                                                onChange={(e) => {
                                                    if (showModal) { setModalTypeNew(e.target.value); }
                                                    if (!showModal) { setModalType(e.target.value); }
                                                    setErrorType('');
                                                }}
                                                onFocus={handleSelectFocus}
                                                onBlur={handleSelectBlur}
                                            >
                                                <option value="">{`${t('reports.selectA2')} ${t('reports.type').toLowerCase()}`} ...</option>
                                                {dataStatusReports.map((status) => (
                                                    <option key={status.NtipoInc} value={status.TtipoInc}>
                                                        {status.TtipoInc}
                                                    </option>
                                                ))}
                                            </select>
                                            <span className="icon">
                                                {isSelectOpen ? (
                                                    <i className="fas fa-chevron-up"></i>
                                                ) : (
                                                    <i className="fas fa-chevron-down"></i>
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                    {errorType && (
                                        <div className="invalid-feedback d-block">{errorType}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6><span>*</span> {t('reports.description')}</h6>
                                        <textarea
                                            className={`form-control ${errorDescription ? 'is-invalid' : ''}`}
                                            value={showModal ? modalDescriptionNew : modalDescription}
                                            onChange={(e) => {
                                                if (showModal) { setModalDescriptionNew(e.target.value); }
                                                if (!showModal) { setModalDescription(e.target.value); }
                                                setErrorDescription('');
                                            }}
                                            rows={3}
                                            placeholder={`${t('reports.enterThe')} ${t('reports.description').toLowerCase()}`}
                                        />
                                    </div>
                                    {errorDescription && (
                                        <div className="invalid-feedback d-block">{errorDescription}</div>
                                    )}
                                </div>
                                <div className='formBtn'>
                                    <button className="btn btn-dark" onClick={() => {
                                        if (showModal) handleCreateReport();
                                        if (showModalEdit) handleUpdateReport();
                                    }}
                                        disabled={loadingBtn}>
                                        {loadingBtn ? (
                                            <div className="loading-btn">
                                                <ClipLoader color="#007bff" size={20} />
                                            </div>
                                        ) : (
                                            showModal ? t('reports.createReport') : showModalEdit ? t('reports.updateReport') : ''
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ReportsComponent;